import { useMemo, useState } from 'react';
import { useQueries, useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { CenteredSpinner } from '../../components/ui/Spinner';
import { Button } from '../../components/ui/button';
import { SelectField } from '../../components/ui/SelectField';
import { Combobox } from '../../components/ui/Combobox';
import { PageHeader } from '../../components/layout/PageHeader';
import { listAccounts } from '../accounts/api';
import type { Account } from '../../types/account';
import type {
  PromotionPlan,
  PromotionPlanStatus,
} from '../../types/promotionPlan';
import { listPlans, listTasks } from './api';

export function planStatusBadge(status: PromotionPlanStatus): string {
  switch (status) {
    case 'draft':
      return 'bg-slate-100 text-slate-700';
    case 'active':
      return 'bg-emerald-100 text-emerald-800';
    case 'completed':
      return 'bg-blue-100 text-blue-800';
    case 'archived':
      return 'bg-slate-200 text-slate-500';
  }
}

// Spec §5.1: default filter is Draft + Active (archived hidden). The status
// dropdown is the single control; "Draft + Active" is the default option and
// "Archived" is just another value (the task brief: archived is a UI toggle,
// i.e. show/hide via this same control — no separate server flag).
type StatusFilter = 'default' | PromotionPlanStatus | 'all';

const STATUS_OPTIONS: Array<{ value: StatusFilter; label: string }> = [
  { value: 'default', label: 'Draft + Active (default)' },
  { value: 'draft', label: 'Draft' },
  { value: 'active', label: 'Active' },
  { value: 'completed', label: 'Completed' },
  { value: 'archived', label: 'Archived' },
  { value: 'all', label: 'All' },
];

function matchesStatusFilter(
  plan: PromotionPlan,
  filter: StatusFilter,
): boolean {
  if (filter === 'all') return true;
  if (filter === 'default') {
    return plan.status === 'draft' || plan.status === 'active';
  }
  return plan.status === filter;
}

function fmtDate(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString();
}

export function PlansPage() {
  const [brandFilter, setBrandFilter] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('default');

  const accounts = useQuery({ queryKey: ['accounts'], queryFn: listAccounts });
  const brands = useMemo<Account[]>(
    () => (accounts.data ?? []).filter((a) => a.accountType === 'brand'),
    [accounts.data],
  );

  // Server filters by brand only; status is filtered client-side so the
  // "Draft + Active" default can span two statuses without an `in` query.
  const plans = useQuery({
    queryKey: ['plans', brandFilter || 'all'],
    queryFn: () =>
      listPlans(brandFilter ? { brandAccountId: brandFilter } : {}),
  });

  const visiblePlans = useMemo(
    () => (plans.data ?? []).filter((p) => matchesStatusFilter(p, statusFilter)),
    [plans.data, statusFilter],
  );

  // Slice A "simple count" task progress per visible plan. Small N (plans
  // per brand) so a per-plan fetch is acceptable — no denormalised count
  // field on the Plan doc (would need maintaining on every task mutation).
  const taskQueries = useQueries({
    queries: visiblePlans.map((p) => ({
      queryKey: ['plan', p.id, 'tasks'],
      queryFn: () => listTasks(p.id),
    })),
  });

  if (plans.isLoading || accounts.isLoading) return <CenteredSpinner />;
  if (plans.error) {
    return (
      <div className="text-red-600 text-sm">
        Failed to load plans: {(plans.error as Error).message}
      </div>
    );
  }

  const brandById = new Map(brands.map((b) => [b.id, b]));

  return (
    <div>
      <PageHeader
        title="Plans"
        description="Promotion plans — the planning workspace that precedes a rollout."
        actions={
          <>
            <Combobox
              aria-label="Filter by brand"
              searchPlaceholder="Search brands…"
              value={brandFilter}
              onValueChange={setBrandFilter}
              options={[
                { value: '', label: 'All brands' },
                ...brands.map((b) => ({ value: b.id, label: b.name })),
              ]}
            />
            <SelectField
              aria-label="Filter by status"
              value={statusFilter}
              onValueChange={(v) => setStatusFilter(v as StatusFilter)}
              options={STATUS_OPTIONS}
            />
            <Button asChild>
              <Link to="/plans/new">Create Plan</Link>
            </Button>
          </>
        }
      />

      {visiblePlans.length === 0 ? (
        <div className="rounded-md border border-dashed border-slate-300 p-8 text-center text-sm text-slate-500">
          {(plans.data ?? []).length === 0
            ? 'No plans yet. Click "Create Plan" to start one.'
            : 'No plans match the current filters.'}
        </div>
      ) : (
        <div className="overflow-hidden rounded-md border border-slate-200 bg-white">
          <table className="min-w-full text-sm">
            <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-4 py-3 font-medium">Name</th>
                <th className="px-4 py-3 font-medium">Brand</th>
                <th className="px-4 py-3 font-medium">Status</th>
                <th className="px-4 py-3 font-medium">Promo dates</th>
                <th className="px-4 py-3 font-medium">Tasks</th>
              </tr>
            </thead>
            <tbody>
              {visiblePlans.map((p, i) => {
                const brand = brandById.get(p.brandAccountId);
                const tasksQ = taskQueries[i];
                const tasks = tasksQ?.data ?? [];
                const done = tasks.filter(
                  (t) => t.status === 'completed',
                ).length;
                return (
                  <tr
                    key={p.id}
                    className="border-t border-slate-100 hover:bg-slate-50"
                  >
                    <td className="px-4 py-2">
                      <Link
                        to={`/plans/${p.id}`}
                        className="font-medium text-slate-900 hover:underline"
                      >
                        {p.name}
                      </Link>
                    </td>
                    <td className="px-4 py-2 text-slate-700">
                      {brand?.name ?? p.brandAccountId}
                    </td>
                    <td className="px-4 py-2">
                      <span
                        className={`text-xs px-2 py-0.5 rounded-full ${planStatusBadge(p.status)}`}
                      >
                        {p.status}
                      </span>
                    </td>
                    <td className="px-4 py-2 text-slate-700">
                      {fmtDate(p.promoStartDate)} → {fmtDate(p.promoEndDate)}
                    </td>
                    <td className="px-4 py-2 text-slate-600">
                      {tasksQ?.isLoading
                        ? '…'
                        : `${done} of ${tasks.length} done`}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
